<template>
  <Teleport to="body">
    <Transition name="panel-fade">
      <div v-if="isPanelOpen" class="notification-panel-overlay" @click.self="closePanel">
        <Transition name="panel-slide">
          <div v-if="isPanelOpen" class="notification-panel">
            <!-- Header -->
            <div class="notification-panel__header">
              <h3 class="notification-panel__title">
                {{ t('notifications.title') }}
                <span v-if="unreadCount > 0" class="notification-panel__badge">
                  {{ unreadCount }}
                </span>
              </h3>
              <div class="notification-panel__actions">
                <button
                  v-if="unreadCount > 0"
                  class="notification-panel__action"
                  @click="handleMarkAllRead"
                >
                  {{ t('notifications.markAllRead') }}
                </button>
                <button
                  class="notification-panel__close"
                  :aria-label="t('common.actions.close')"
                  @click="closePanel"
                >
                  <XIcon :size="20" />
                </button>
              </div>
            </div>

            <!-- List -->
            <div class="notification-panel__list" @scroll="handleScroll">
              <template v-if="isLoading && notifications.length === 0">
                <div class="notification-panel__loading">
                  <div class="notification-panel__spinner" />
                </div>
              </template>

              <template v-else-if="notifications.length === 0">
                <div class="notification-panel__empty">
                  <BellOffIcon :size="40" />
                  <p>{{ t('notifications.empty') }}</p>
                </div>
              </template>

              <template v-else>
                <NotificationItem
                  v-for="notif in notifications"
                  :key="notif.id"
                  :notification="notif"
                  @click="handleNotificationClick"
                />

                <div v-if="isLoading" class="notification-panel__loading-more">
                  <div class="notification-panel__spinner notification-panel__spinner--small" />
                </div>
              </template>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { XIcon, BellOffIcon } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import NotificationItem from './NotificationItem.vue'
import { useNotifications } from '../composables/useNotifications'
import { buildNotificationRoute } from '../utils/notificationRoute'
import type { Notification } from '../types/notifications.types'

const router = useRouter()
const { t } = useI18n()
const {
  notifications,
  unreadCount,
  isLoading,
  isPanelOpen,
  closePanel,
  markRead,
  markAllRead,
  loadMore,
} = useNotifications()

function handleNotificationClick(notification: Notification) {
  if (!notification.isRead) {
    markRead(notification.id)
  }

  const target = buildNotificationRoute(notification)
  if (target) {
    closePanel()
    router.push(target)
  }
}

async function handleMarkAllRead() {
  await markAllRead()
}

function handleScroll(event: Event) {
  const el = event.target as HTMLElement
  const threshold = 100
  if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
    loadMore()
  }
}
</script>

<style scoped>
.notification-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(9, 9, 11, 0.2);
}
.notification-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 100vw;
  background: #fff;
  box-shadow: -4px 0 24px rgba(9, 9, 11, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 61;
}
.notification-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border, #ecedf1);
  flex-shrink: 0;
}
.notification-panel__title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #18181b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.notification-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #4f46e5;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
}
.notification-panel__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.notification-panel__action {
  padding: 4px 10px;
  border: none;
  background: none;
  color: #4f46e5;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
}
.notification-panel__action:hover {
  background: #eef2ff;
}
.notification-panel__close {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: var(--color-text-secondary, #52525b);
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.notification-panel__close:hover {
  background: var(--color-background-mute, #f4f4f5);
  color: #18181b;
}
.notification-panel__list {
  flex: 1;
  overflow-y: auto;
}
.notification-panel__loading,
.notification-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: var(--color-text-tertiary, #a1a1aa);
  gap: 12px;
}
.notification-panel__empty p {
  margin: 0;
  font-size: 0.875rem;
}
.notification-panel__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border, #ecedf1);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.notification-panel__spinner--small {
  width: 20px;
  height: 20px;
  border-width: 2px;
}
.notification-panel__loading-more {
  display: flex;
  justify-content: center;
  padding: 16px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.2s ease;
}
.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}
.panel-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.panel-slide-leave-active {
  transition: transform 0.2s ease-in;
}
.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(100%);
}
</style>
